import type { BinaryInput, SymbolInput, ContinuousInput, InputFormat } from '@/types'

export type ParseResult =
  | { kind: 'binary'; input: BinaryInput }
  | { kind: 'symbol'; input: SymbolInput }
  | { kind: 'continuous'; input: ContinuousInput }

/**
 * Pack individual bits (0/1 values) into Uint8Array (8 bits per byte, MSB first).
 */
export function packBits(bits: number[] | Uint8Array): { data: Uint8Array; bitLength: number } {
  const bitLength = bits.length
  const byteLength = Math.ceil(bitLength / 8)
  const data = new Uint8Array(byteLength)

  for (let i = 0; i < bitLength; i++) {
    if (bits[i] === 1) {
      data[i >>> 3] |= (0x80 >>> (i & 7))
    }
  }

  return { data, bitLength }
}

/**
 * Compute SHA-256 hash of data using SubtleCrypto.
 */
export async function computeSHA256(data: ArrayBuffer): Promise<string> {
  const hashBuffer = await crypto.subtle.digest('SHA-256', data)
  const hashArray = new Uint8Array(hashBuffer)
  return Array.from(hashArray)
    .map((b) => b.toString(16).padStart(2, '0'))
    .join('')
}

/**
 * Check if text looks like prose, code, markup, or other non-data content.
 * Returns a description string if it matches, or null if it looks like data.
 */
function detectNonDataContent(text: string): string | null {
  const trimmed = text.trim()

  // JSON detection
  if ((trimmed.startsWith('{') && trimmed.endsWith('}')) ||
      (trimmed.startsWith('[') && trimmed.endsWith(']'))) {
    try {
      JSON.parse(trimmed)
      return 'JSON'
    } catch {
      // Not valid JSON, but still looks like it
      if (/^\s*[{\[]/.test(trimmed) && /[}\]]\s*$/.test(trimmed)) {
        return 'JSON'
      }
    }
  }

  // HTML/XML detection
  if (/<[a-zA-Z][^>]*>/.test(text)) {
    return 'HTML/XML markup'
  }

  // Excel formula detection
  if (/^=\s*[A-Z]+\(/.test(trimmed)) {
    return 'a spreadsheet formula'
  }

  // Check for common code/prose keywords
  const codeWords = new Set([
    'import', 'export', 'function', 'class', 'const', 'let', 'var', 'return',
    'if', 'else', 'for', 'while', 'print', 'def', 'html', 'body', 'div',
    'span', 'script', 'style', 'http', 'https', 'www', 'require', 'module',
    'async', 'await', 'try', 'catch', 'throw', 'new', 'this', 'void',
    'null', 'true', 'false', 'undefined', 'typeof', 'instanceof',
    'package', 'public', 'private', 'protected', 'static', 'final',
    'include', 'using', 'namespace', 'struct', 'enum', 'interface',
    'lambda', 'from', 'with', 'assert', 'yield', 'pass', 'raise',
    'except', 'finally', 'elif', 'del', 'global', 'nonlocal',
    'the', 'and', 'that', 'have', 'for', 'not', 'with', 'you', 'this',
    'but', 'his', 'they', 'say', 'she', 'will', 'would', 'there',
    'their', 'what', 'about', 'which', 'when', 'make', 'like', 'time',
    'just', 'know', 'take', 'people', 'into', 'could', 'than', 'other',
  ])

  // Find words (sequences of 3+ letters)
  const words = text.match(/[a-zA-Z]{3,}/g)
  if (words && words.length > 3) {
    const hasCodeWords = words.some(w => codeWords.has(w.toLowerCase()))
    if (hasCodeWords) return 'code or natural language text'

    // If significant portion of text is non-hex alphabetic chars, it's prose
    const nonHexAlpha = text.replace(/[0-9a-fA-F\s\r\n]/g, '')
    if (nonHexAlpha.length > text.length * 0.1) return 'non-numeric text'
  }

  // Check for non-hex alphabetic characters mixed with text
  // (e.g., "Hello world" has 'l', 'o', 'w', 'r' which are not hex)
  const nonWhitespace = text.replace(/\s/g, '')
  if (nonWhitespace.length > 0) {
    const nonHexNonDigit = nonWhitespace.replace(/[0-9a-fA-F]/g, '')
    // If there are non-hex characters and text has word-like patterns with spaces
    if (nonHexNonDigit.length > 0 && /[a-zA-Z]{2,}\s+[a-zA-Z]{2,}/.test(text)) {
      return 'non-numeric text'
    }
  }

  return null
}

/**
 * Check if a hex string is strictly valid data (not accidentally matching prose).
 * Requires: all hex chars, even length, minimum 2 chars, and must have
 * some non-decimal digit hex chars (a-f) or look intentionally hex-like.
 */
function isStrictHex(stripped: string): boolean {
  // Must be non-empty, all hex, even length, at least 2 chars
  if (stripped.length < 2) return false
  if (stripped.length % 2 !== 0) return false
  if (!/^[0-9a-fA-F]+$/.test(stripped)) return false

  // If the stripped text is purely decimal digits, it's not hex
  // (it would have been caught by integer/float detection already)
  if (/^[0-9]+$/.test(stripped)) return false

  return true
}

/**
 * Detect the format of file content based on content analysis and file extension.
 */
export function detectFormat(content: ArrayBuffer, fileName: string): InputFormat {
  const ext = fileName.split('.').pop()?.toLowerCase() ?? ''

  // .bin extension → raw binary
  if (ext === 'bin') {
    return 'raw-bytes'
  }

  const bytes = new Uint8Array(content)

  // Empty file
  if (bytes.length === 0) {
    throw new Error('No data found. Please upload a file containing numerical data (binary 0/1, CSV, or raw binary).')
  }

  // Try to decode as text for text-based format detection
  let text: string
  try {
    const decoder = new TextDecoder('utf-8', { fatal: true })
    text = decoder.decode(content)
  } catch {
    // Not valid UTF-8 → treat as raw binary
    return 'raw-bytes'
  }

  // Check if the text content is ASCII 0/1 with only whitespace
  const stripped = text.replace(/[\s\r\n]/g, '')
  if (stripped.length > 0 && /^[01]+$/.test(stripped)) {
    return 'ascii01'
  }

  // Early JSON detection — must come before CSV to avoid "[1,2,3]" being parsed as CSV
  const trimmedText = text.trim()
  if ((trimmedText.startsWith('{') && trimmedText.endsWith('}')) ||
      (trimmedText.startsWith('[') && trimmedText.endsWith(']'))) {
    try {
      JSON.parse(trimmedText)
      throw new Error('JSON format is not supported. Please export your data as CSV or plain text.')
    } catch (e) {
      if (e instanceof Error && e.message.includes('JSON format is not supported')) {
        throw e
      }
      // Not valid JSON, continue with other detection
    }
  }

  // Helper predicates used for structured format detection
  const isFloat = (s: string): boolean => {
    const n = Number(s)
    return !isNaN(n) && s.trim() !== '' && (s.includes('.') || s.includes('e') || s.includes('E'))
  }

  const isInteger = (s: string): boolean => {
    const n = Number(s)
    return !isNaN(n) && Number.isInteger(n) && s.trim() !== ''
  }

  // Check for CSV/TSV/space-separated content BEFORE hex detection,
  // because line-structured numeric data (e.g. "1\n0\n255\n128") can
  // look like a hex string once whitespace is stripped.
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '')
  if (lines.length > 0) {
    const firstLine = lines[0].trim()
    const hasComma = firstLine.includes(',')
    const hasSemicolon = firstLine.includes(';')
    const hasTab = firstLine.includes('\t')

    if (hasTab) {
      const values = firstLine.split('\t').map((v) => v.trim())
      if (values.length >= 1 && values.every((v) => isFloat(v) || isInteger(v))) {
        if (values.some((v) => isFloat(v))) {
          return 'float-tsv'
        }
      }
      return 'float-tsv'
    }

    if (hasComma) {
      const values = firstLine.split(',').map((v) => v.trim())
      // Check if this looks like a CSV header (e.g., "a,b" with letter-only columns)
      const hasHeader = values.some((v) => /^[a-zA-Z_][a-zA-Z0-9_]*$/.test(v))
      if (hasHeader && lines.length > 1) {
        // Skip header, check data lines
        const dataLines = lines.slice(1)
        const dataFirstLine = dataLines[0].trim()
        const dataValues = dataFirstLine.split(',').map((v) => v.trim())
        if (dataValues.some((v) => isFloat(v))) {
          return 'float-csv'
        }
        if (dataValues.every((v) => isInteger(v))) {
          return 'csv'
        }
        return 'csv'
      }
      if (values.length === 1) {
        const allInts = lines.every((l) => {
          const v = l.split(',')[0].trim()
          return isInteger(v)
        })
        if (allInts) {
          return 'csv'
        }
        return 'float-csv'
      }
      if (values.some((v) => isFloat(v))) {
        return 'float-csv'
      }
      return 'csv'
    }

    // Semicolon-delimited (European convention)
    if (hasSemicolon) {
      const values = firstLine.split(';').map((v) => v.trim())
      if (values.length > 1) {
        // European format: "1250,3" means 1250.3 when delimiter is ;
        const isEuroFloat = (s: string): boolean => {
          const normalized = s.replace(',', '.')
          return isFloat(normalized) || isInteger(normalized)
        }
        if (values.every((v) => isEuroFloat(v))) {
          if (values.some((v) => isFloat(v.replace(',', '.')))) {
            return 'float-csv'
          }
          return 'csv'
        }
      }
    }

    // No comma/tab: single value per line or space-separated
    if (lines.length > 1) {
      const singleValues = lines.every((l) => {
        const parts = l.trim().split(/\s+/)
        return parts.length === 1
      })

      if (singleValues) {
        const allInts = lines.every((l) => isInteger(l.trim()))
        if (allInts) {
          return 'csv'
        }
        const allFloats = lines.every((l) => {
          const v = l.trim()
          return isFloat(v) || isInteger(v)
        })
        if (allFloats) {
          return 'float-csv'
        }
      } else {
        const firstValues = firstLine.split(/\s+/).map((v) => v.trim())
        if (firstValues.some((v) => isFloat(v))) {
          return 'float-space'
        }
      }
    }
  }

  // Check for prose, code, markup, and other non-data content BEFORE hex
  const nonDataType = detectNonDataContent(text)
  if (nonDataType) {
    if (nonDataType === 'JSON') {
      throw new Error('JSON format is not supported. Please export your data as CSV or plain text.')
    }
    throw new Error(
      `Could not detect a supported data format. Expected: binary bitstream (0/1), hex string, CSV/TSV with numeric columns, or raw binary (.bin). Received text that appears to be ${nonDataType}.`
    )
  }

  // Check if it is a hex string — strict validation to avoid
  // misclassifying text that happens to contain only hex chars.
  if (isStrictHex(stripped)) {
    return 'hex'
  }

  // If we still have text that's all hex chars but failed strict check
  // (odd length, too short, etc.), it's unrecognized
  if (stripped.length > 0 && /^[0-9a-fA-F]+$/.test(stripped)) {
    // Single hex char or odd-length hex — still accept if clearly intentional
    // (e.g., file extension .hex)
    if (ext === 'hex') {
      return 'hex'
    }
    // Otherwise reject as ambiguous
    throw new Error(
      'Could not detect a supported data format. Expected: binary bitstream (0/1), hex string (even number of hex characters), CSV/TSV with numeric columns, or raw binary (.bin).'
    )
  }

  // If we get here with text content, it's unrecognized
  if (stripped.length > 0) {
    throw new Error(
      'Could not detect a supported data format. Expected: binary bitstream (0/1), hex string, CSV/TSV with numeric columns, or raw binary (.bin). Received text that appears to be non-numeric text.'
    )
  }

  // Fallback: raw binary
  return 'raw-bytes'
}

/**
 * Parse ASCII 0/1 string into BinaryInput.
 */
function parseAscii01(text: string, fileName: string, fileSize: number): ParseResult {
  const stripped = text.replace(/[\s\r\n]/g, '')
  if (stripped.length === 0) {
    throw new Error('No data found. Please upload a file containing numerical data (binary 0/1, CSV, or raw binary).')
  }
  if (!/^[01]+$/.test(stripped)) {
    throw new Error('Invalid ASCII 0/1 input: contains characters other than 0 and 1.')
  }

  const bits = new Uint8Array(stripped.length)
  for (let i = 0; i < stripped.length; i++) {
    bits[i] = stripped.charCodeAt(i) - 0x30
  }

  const { data, bitLength } = packBits(bits)

  return {
    kind: 'binary',
    input: {
      data,
      bitLength,
      format: 'ascii01',
      sampleCount: bitLength,
      fileName,
      fileSize,
    },
  }
}

/**
 * Parse hex string into BinaryInput.
 */
function parseHex(text: string, fileName: string, fileSize: number): ParseResult {
  const stripped = text.replace(/[\s\r\n]/g, '')
  if (stripped.length === 0) {
    throw new Error('Empty hex input: no characters found after stripping whitespace.')
  }
  if (!/^[0-9a-fA-F]+$/.test(stripped)) {
    throw new Error('Invalid hex input: contains non-hexadecimal characters.')
  }

  // Each hex char = 4 bits
  const bitLength = stripped.length * 4
  const byteLength = Math.ceil(bitLength / 8)
  const data = new Uint8Array(byteLength)

  // Pad to even length for byte-pair processing
  const padded = stripped.length % 2 === 0 ? stripped : stripped + '0'
  for (let i = 0; i < padded.length; i += 2) {
    data[i >>> 1] = parseInt(padded.substring(i, i + 2), 16)
  }

  return {
    kind: 'binary',
    input: {
      data,
      bitLength,
      format: 'hex',
      sampleCount: bitLength,
      fileName,
      fileSize,
    },
  }
}

/**
 * Parse raw binary bytes into BinaryInput.
 */
function parseRawBinary(buffer: ArrayBuffer, fileName: string, fileSize: number): ParseResult {
  const data = new Uint8Array(buffer)
  const bitLength = data.length * 8

  return {
    kind: 'binary',
    input: {
      data: new Uint8Array(data),
      bitLength,
      format: 'raw-bytes',
      sampleCount: bitLength,
      fileName,
      fileSize,
    },
  }
}

/**
 * Parse CSV with integer values.
 * If all values are 0 or 1 → BinaryInput (packed).
 * If values are 0-255 → SymbolInput.
 */
function parseCsvIntegers(text: string, fileName: string, fileSize: number): ParseResult {
  const MAX_SAMPLES = 20_000_000 // 20M samples max to prevent OOM
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '')
  const values: number[] = []

  // Skip header row if first line contains non-numeric values
  let startIdx = 0
  if (lines.length > 1) {
    const firstParts = lines[0].split(',').map(p => p.trim())
    const hasNonNumeric = firstParts.some(p => p !== '' && isNaN(Number(p)))
    if (hasNonNumeric) {
      startIdx = 1
    }
  }

  for (let i = startIdx; i < lines.length; i++) {
    const line = lines[i]
    const parts = line.split(',')
    for (const part of parts) {
      const trimmed = part.trim()
      if (trimmed === '') continue
      const num = Number(trimmed)
      if (isNaN(num) || !Number.isInteger(num)) {
        throw new Error(`Invalid integer value in CSV: "${trimmed}"`)
      }
      values.push(num)
      if (values.length >= MAX_SAMPLES) break
    }
    if (values.length >= MAX_SAMPLES) break
  }

  if (values.length === 0) {
    throw new Error('Empty CSV input: no values found.')
  }

  // Check if all values are 0 or 1
  const allBinary = values.every((v) => v === 0 || v === 1)
  if (allBinary) {
    const { data, bitLength } = packBits(values)
    return {
      kind: 'binary',
      input: {
        data,
        bitLength,
        format: 'csv',
        sampleCount: bitLength,
        fileName,
        fileSize,
      },
    }
  }

  // Check if all values are 0-255
  const allBytes = values.every((v) => v >= 0 && v <= 255)
  if (allBytes) {
    const data = new Uint8Array(values)
    return {
      kind: 'symbol',
      input: {
        data,
        symbolSize: 8,
        alphabetSize: 256,
        format: 'csv',
        sampleCount: values.length,
        fileName,
        fileSize,
      },
    }
  }

  throw new Error('CSV integer values out of range. Expected 0-1 for binary or 0-255 for symbol input.')
}

/**
 * Parse floating-point values from text into ContinuousInput.
 */
function parseFloats(text: string, format: InputFormat, fileName: string, fileSize: number): ParseResult {
  const MAX_SAMPLES = 5_000_000 // 5M float samples max to prevent OOM
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== '')
  const values: number[] = []

  let delimiter: RegExp
  // Detect if this is semicolon-delimited (European convention)
  const isSemicolonDelimited = lines.length > 0 && lines[0].includes(';')
  switch (format) {
    case 'float-csv':
      delimiter = isSemicolonDelimited ? /;/ : /,/
      break
    case 'float-tsv':
      delimiter = /\t/
      break
    case 'float-space':
      delimiter = /\s+/
      break
    default:
      delimiter = /[,\t\s]+/
  }

  // Skip header row if first line contains non-numeric values
  let startIdx = 0
  if (lines.length > 1) {
    const firstParts = lines[0].trim().split(delimiter).map(p => p.trim())
    const hasNonNumeric = firstParts.some(p => p !== '' && isNaN(Number(p)) && isNaN(Number(p.replace(',', '.'))))
    if (hasNonNumeric) {
      startIdx = 1
    }
  }

  for (let i = startIdx; i < lines.length; i++) {
    const line = lines[i]
    const parts = line.trim().split(delimiter)
    for (const part of parts) {
      let trimmed = part.trim()
      if (trimmed === '') continue
      // European number format: replace comma with dot when using semicolon delimiter
      if (isSemicolonDelimited && trimmed.includes(',')) {
        trimmed = trimmed.replace(',', '.')
      }
      const num = Number(trimmed)
      if (isNaN(num)) {
        throw new Error(`Invalid numeric value: "${trimmed}"`)
      }
      values.push(num)
      if (values.length >= MAX_SAMPLES) break
    }
    if (values.length >= MAX_SAMPLES) break
  }

  if (values.length === 0) {
    throw new Error('Empty float input: no values found.')
  }

  return {
    kind: 'continuous',
    input: {
      data: new Float64Array(values),
      format,
      sampleCount: values.length,
      fileName,
      fileSize,
    },
  }
}

/**
 * Parse a File object, auto-detecting format.
 */
export async function parseFile(file: File): Promise<ParseResult> {
  const buffer = await file.arrayBuffer()
  const format = detectFormat(buffer, file.name)

  return parseBuffer(buffer, format, file.name, file.size)
}

function parseBuffer(buffer: ArrayBuffer, format: InputFormat, fileName: string, fileSize: number): ParseResult {
  if (format === 'raw-bytes') {
    return parseRawBinary(buffer, fileName, fileSize)
  }

  const decoder = new TextDecoder('utf-8')
  const text = decoder.decode(buffer)

  switch (format) {
    case 'ascii01':
      return parseAscii01(text, fileName, fileSize)
    case 'hex':
      return parseHex(text, fileName, fileSize)
    case 'csv':
      return parseCsvIntegers(text, fileName, fileSize)
    case 'float-csv':
    case 'float-tsv':
    case 'float-space':
      return parseFloats(text, format, fileName, fileSize)
    case 'binary':
      return parseRawBinary(buffer, fileName, fileSize)
    default: {
      const _exhaustive: never = format
      throw new Error(`Unsupported format: ${_exhaustive}`)
    }
  }
}

/**
 * Parse text directly (e.g., from clipboard paste), with optional format hint.
 */
export function parseText(text: string, formatHint?: InputFormat): ParseResult {
  const encoder = new TextEncoder()
  const buffer = encoder.encode(text).buffer as ArrayBuffer
  const format = formatHint ?? detectFormat(buffer, 'pasted-text.txt')
  const fileSize = buffer.byteLength

  return parseBuffer(buffer, format, 'pasted-text', fileSize)
}

/**
 * Check if an ArrayBuffer contains multi-column text data (CSV, TSV, etc.).
 * Returns the decoded text alongside the detection result.
 */
export function isMultiColumnText(content: ArrayBuffer, fileName: string): { isMultiColumn: boolean; text: string } {
  // Return false for .bin files
  if (fileName.toLowerCase().endsWith('.bin')) {
    return { isMultiColumn: false, text: '' }
  }

  // Try to decode as UTF-8
  try {
    const decoder = new TextDecoder('utf-8', { fatal: true })
    const text = decoder.decode(content)

    // Check if it's simple binary (all 0/1 + whitespace) or strict hex
    const stripped = text.replace(/\s/g, '')
    if (/^[01]+$/.test(stripped)) return { isMultiColumn: false, text }
    if (isStrictHex(stripped)) return { isMultiColumn: false, text }

    // Check for multi-column structure
    const lines = text.split(/\r?\n/).filter((l) => l.trim() && !l.trim().startsWith('#'))
    if (lines.length < 2) return { isMultiColumn: false, text }

    for (const delim of [',', '\t', ';', '|']) {
      const counts = lines.slice(0, 5).map((l) => l.split(delim).length)
      if (counts[0]! > 1 && counts.every((c) => c === counts[0])) {
        return { isMultiColumn: true, text }
      }
    }

    return { isMultiColumn: false, text }
  } catch {
    return { isMultiColumn: false, text: '' }
  }
}
